import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import css from './CheckoutPlanPage.css';
import config from '../../config';
import PageWrapper from '../../custom-components/wrappers/page-wrapper/PageWrapper';
import { getReferralCode, clearReferralCode } from '../../util/referral';
import { isValidPostalCode } from '../../util/validators';
import getCountryCodes from '../../translations/countryCodes';
import { PLAN_CATALOG, resolvePlanId } from '../GetStartedPage2/planCatalog';
// Coach lockup (purple badge), not the marketplace/teal one — this is a
// coach subscription checkout, and viveka-logos.js's own usage rules say
// V-Coach flows get the coach lockup.
import { coachLockupLogo } from '../../assets/viveka-logos';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COMPONENT_LOAD_TIMEOUT_MS = 6000;
const COUNTRIES = getCountryCodes(config.locale);

// Full-page checkout for a single plan, reached from /get-started's plan
// cards. This replaces what used to be an on-page popup with the same
// fields — the Chargebee Payment Components integration below is carried
// over unchanged from that popup (see GetStartedPage2.js's file-level
// comment for the history of getting this integration right). Only the UI
// shell changed: a two-column page (plan description left, payment card
// right) instead of a modal overlay.
// Chargebee's Payment Component can't be safely recreated mid-session
// (confirmed live: a second Chargebee.init() + components.create('payment')
// after one is already mounted broke the card form entirely — "Payment
// form unavailable", card fields cut off). So applying a coupon triggers a
// real page reload instead of an in-page remount, giving Chargebee a
// completely fresh JS context. Form fields + the coupon are stashed here
// across that reload and restored via the lazy useState initializers below.
function storageKeyFor(tier) {
  return `checkout-plan-saved:${tier}`;
}

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  addressLine1: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
};

function readSavedCheckout(tier, urlCoupon) {
  if (typeof window === 'undefined' || !urlCoupon) return null;
  try {
    const raw = window.sessionStorage.getItem(storageKeyFor(tier));
    if (!raw) return null;
    const saved = JSON.parse(raw);
    // Only trust it if it matches the coupon actually in the URL — avoids
    // leaking a stale save from a previous/different coupon attempt.
    return saved && saved.couponCode === urlCoupon ? saved : null;
  } catch (e) {
    return null;
  }
}

function CheckoutPlanPage(props) {
  const tier = props.params && props.params.tier;
  const period =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('period') === 'annual'
        ? 'annual'
        : 'monthly'
      : 'monthly';
  const urlCoupon =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('coupon') : null;

  const plan = PLAN_CATALOG[tier];
  const planId = resolvePlanId(tier, period);
  const isAnnual = period === 'annual';
  const savedCheckout = readSavedCheckout(tier, urlCoupon);

  const [lifetime, setLifetime] = useState({ seatsRemaining: null, soldOut: false });
  const [form, setForm] = useState(() => (savedCheckout ? savedCheckout.form : EMPTY_FORM));
  const [formErrors, setFormErrors] = useState({});
  const [pageError, setPageError] = useState(null);
  const [cardReady, setCardReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const paymentComponentRef = useRef(null);

  const [couponCode, setCouponCode] = useState(() => (savedCheckout ? savedCheckout.couponCode : ''));
  const [couponChecking, setCouponChecking] = useState(false);
  const [couponError, setCouponError] = useState(null);
  // Set when a coupon is valid but scoped (in Chargebee) to a different
  // plan than this one — [{ tier, period, label }, ...] the coupon IS
  // valid for, so we can offer a direct "switch to this plan" link instead
  // of just saying "invalid".
  const [couponEligiblePlans, setCouponEligiblePlans] = useState(null);
  const [couponPreview, setCouponPreview] = useState(() => (savedCheckout ? savedCheckout.couponPreview : null)); // { discountAmount, originalPrice, finalPrice, currencyCode } | null
  const appliedCouponCode = couponPreview ? couponCode.trim() : null;

  // onSuccess/onError are registered once (inside the mount effect below)
  // but need the *current* form/coupon values at confirm() time — refs
  // avoid the closure capturing stale state from when the effect first ran.
  const formRef = useRef(form);
  useEffect(() => {
    formRef.current = form;
  }, [form]);
  const appliedCouponRef = useRef(null);
  useEffect(() => {
    appliedCouponRef.current = appliedCouponCode;
  }, [appliedCouponCode]);

  useEffect(() => {
    if (tier !== 'lifetime') return;
    axios
      .post(`${config.subscriptionServiceUrl}eligible-plans`, {})
      .then(res => {
        const data = res.data || {};
        const seatsRemaining = data.lifetime && data.lifetime.seatsRemaining;
        setLifetime({
          seatsRemaining: typeof seatsRemaining === 'number' ? seatsRemaining : null,
          soldOut: seatsRemaining === 0,
        });
      })
      .catch(() => {
        // Best-effort — if this fails we just show the page without a seat count.
      });
  }, [tier]);

  // Navigates to this same page with ?coupon=<code> (preserving ?period=),
  // after stashing the current form + preview in sessionStorage so they
  // survive the reload. See the file-level comment above for why this is a
  // real navigation instead of an in-page state update.
  const reloadWithCoupon = (code, preview) => {
    try {
      window.sessionStorage.setItem(
        storageKeyFor(tier),
        JSON.stringify({ form: formRef.current, couponCode: code, couponPreview: preview })
      );
    } catch (e) {
      // Best-effort — worst case the customer just has to retype the form
      // after the reload.
    }
    const params = new URLSearchParams(window.location.search);
    params.set('coupon', code);
    window.location.href = `${window.location.pathname}?${params.toString()}`;
  };

  const applyCoupon = () => {
    const code = couponCode.trim();
    setCouponPreview(null);
    setCouponError(null);
    setCouponEligiblePlans(null);
    if (!code || !planId) return;

    setCouponChecking(true);
    axios
      .post(`${config.subscriptionServiceUrl}coupon-preview`, { plan: planId, coupon: code })
      .then(res => {
        setCouponChecking(false);
        const data = res.data || {};
        if (!data.valid) {
          setCouponError(data.message || 'This coupon is not valid.');
          setCouponEligiblePlans(data.eligiblePlans && data.eligiblePlans.length > 0 ? data.eligiblePlans : null);
          return;
        }
        reloadWithCoupon(code, data);
      })
      .catch(() => {
        setCouponChecking(false);
        setCouponError("Couldn't check that coupon right now — please try again.");
      });
  };

  // Reloads back to the plain (no-coupon) URL — same reasoning as applying
  // one: the payment component was mounted for the discounted amount, so
  // removing the coupon needs an equally fresh mount for full price.
  const removeCoupon = () => {
    try {
      window.sessionStorage.removeItem(storageKeyFor(tier));
    } catch (e) {
      // Ignore — the stale entry just won't match next time (couponCode
      // check in readSavedCheckout) and will be disregarded anyway.
    }
    const params = new URLSearchParams(window.location.search);
    params.delete('coupon');
    const query = params.toString();
    window.location.href = `${window.location.pathname}${query ? `?${query}` : ''}`;
  };

  // Formats a smallest-currency-unit amount (cents for USD) as "$X.XX".
  const formatMoney = (cents, currencyCode) => {
    const amount = (cents / 100).toFixed(2);
    return currencyCode === 'USD' || !currencyCode ? `$${amount}` : `${amount} ${currencyCode}`;
  };

  // Creates a Payment Intent for this plan — for the coupon-discounted
  // amount if ?coupon= is present in the URL (see applyCoupon/reloadWithCoupon
  // above for why that's a URL param and not just React state), otherwise
  // full price — then mounts Chargebee's real Payment Component against it,
  // exactly once. COMPONENT_LOAD_TIMEOUT_MS is a fallback: if it hasn't
  // mounted by then, show a clear message instead of leaving a broken/
  // blank-looking form.
  //
  // Deliberately does NOT re-run when the coupon fields change — an earlier
  // attempt at recreating the intent/component in place (mid-session, no
  // reload) broke Chargebee's Payment Component entirely (mount partially
  // failed, "Payment form unavailable"), most likely because Chargebee.init()
  // + a second components.create('payment', ...) isn't safe to call again on
  // the same page once a component is already mounted. If the coupon turns
  // out invalid server-side (self-referral etc.), that's resolved by falling
  // back to a plain full-price intent *before* ever touching the Chargebee
  // SDK below — never a second mount attempt.
  useEffect(() => {
    if (!planId) return;
    if (typeof window === 'undefined' || !window.Chargebee) {
      setPageError("Payment form isn't available right now — please refresh and try again.");
      return;
    }

    let cancelled = false;
    const cbInstance = window.Chargebee.init({
      site: config.chargebeeSiteName,
      publishableKey: config.chargebeePublishableKey,
    });

    const timeoutId = setTimeout(() => {
      if (cancelled || paymentComponentRef.current) return;
      setPageError('Payment form unavailable — please contact support.');
    }, COMPONENT_LOAD_TIMEOUT_MS);

    // Redeems an authorized Payment Intent for a real subscription. Reads
    // current form/coupon values via refs (see their declarations above)
    // since onSuccess is registered once here but can fire long after, once
    // the shopper has actually typed into the form.
    const finishSubscription = paymentIntentId => {
      const {
        firstName,
        lastName,
        email,
        addressLine1,
        city,
        state,
        zip,
        country,
      } = formRef.current;
      // manualCoupon: typed in and confirmed via /coupon-preview — a
      // failure here must block checkout (see /checkout/subscribe-with-intent).
      // referralCoupon: silently carried over from a past ?ref= link visit,
      // never seen or chosen by this customer — sent separately so the
      // backend can treat its failure (self-referral, expired, deleted) as
      // "no discount" instead of blocking the subscription outright.
      const manualCoupon = appliedCouponRef.current;
      const referralCoupon = manualCoupon ? null : getReferralCode();
      axios
        .post(`${config.subscriptionServiceUrl}checkout/subscribe-with-intent`, {
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          addressLine1: addressLine1.trim(),
          city: city.trim(),
          state: state.trim(),
          zip: zip.trim(),
          country: country || 'US',
          plan: planId,
          paymentIntentId,
          ...(manualCoupon ? { coupon: manualCoupon } : {}),
          ...(referralCoupon ? { referralCoupon } : {}),
        })
        .then(res => {
          const data = res.data || {};
          if (!data.ok || !data.subscriptionId) {
            throw new Error(data.message || 'subscribe_failed');
          }
          clearReferralCode();
          window.location.href = `${window.location.origin}/checkout/complete?subscriptionId=${encodeURIComponent(
            data.subscriptionId
          )}`;
        })
        .catch(err => {
          setSubmitting(false);
          setPageError(
            (err && err.response && err.response.data && err.response.data.message) ||
              (err && err.message) ||
              "We couldn't process that card. Please check the details and try again."
          );
        });
    };

    const createIntent = coupon =>
      axios.post(`${config.subscriptionServiceUrl}payment-intent`, {
        plan: planId,
        ...(coupon ? { coupon, email: formRef.current.email.trim() } : {}),
      });

    const mountPaymentComponent = paymentIntent => {
      const components = cbInstance.components({});
      const paymentComponent = components.create(
        'payment',
        {
          paymentIntent,
          layout: { type: 'tab' },
          paymentMethods: { allowed: ['card'] },
        },
        {
          onError: error => {
            setSubmitting(false);
            setPageError(
              (error && error.message) || "We couldn't process that card. Please check the details and try again."
            );
          },
          onSuccess: authorizedIntent => {
            finishSubscription(authorizedIntent.id);
          },
        }
      );

      return paymentComponent.mount('#checkout-plan-payment-component').then(mounted => {
        if (cancelled) return;
        if (!mounted) throw new Error('mount_failed');
        paymentComponentRef.current = paymentComponent;
        clearTimeout(timeoutId);
        setCardReady(true);
      });
    };

    createIntent(urlCoupon)
      .catch(err => {
        // A coupon that was valid at preview but rejected here (self-
        // referral, expired since the reload, etc.) falls back to a plain
        // full-price intent — this happens before the Chargebee SDK is
        // ever touched, so there's still only one mount attempt total.
        if (urlCoupon && err && err.response && err.response.data && err.response.data.error === 'invalid_coupon') {
          if (!cancelled) {
            const errData = err.response.data;
            setCouponError(errData.message || 'That coupon could not be applied.');
            setCouponEligiblePlans(errData.eligiblePlans && errData.eligiblePlans.length > 0 ? errData.eligiblePlans : null);
            setCouponPreview(null);
          }
          return createIntent(null);
        }
        throw err;
      })
      .then(res => {
        if (cancelled) return;
        const paymentIntent = res.data && res.data.payment_intent;
        if (!paymentIntent) throw new Error('payment_intent_failed');
        return mountPaymentComponent(paymentIntent);
      })
      .catch(() => {
        if (!cancelled) {
          clearTimeout(timeoutId);
          setPageError('Payment form unavailable — please contact support.');
        }
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // urlCoupon only ever changes via a full page reload (see
    // reloadWithCoupon/removeCoupon above), which re-initializes this
    // whole component fresh anyway — listing it here is for correctness,
    // not because it's expected to change within one mount's lifetime.
  }, [planId, urlCoupon]);

  // confirm() doesn't validate the form itself and doesn't return the
  // result directly — outcomes arrive via the onSuccess/onError callbacks
  // registered when the Payment Component was created (see the mount
  // effect above), which is where the actual subscribe call happens.
  const handleSubmit = async e => {
    e.preventDefault();
    setPageError(null);

    const errors = {};
    if (!form.firstName.trim()) errors.firstName = 'Required';
    if (!form.lastName.trim()) errors.lastName = 'Required';
    if (!EMAIL_RE.test(form.email.trim())) errors.email = 'Enter a valid email';
    if (!form.addressLine1.trim()) errors.addressLine1 = 'Required';
    if (!form.city.trim()) errors.city = 'Required';
    if (!form.state.trim()) errors.state = 'Required';
    if (!form.zip.trim()) {
      errors.zip = 'Required';
    } else if (!isValidPostalCode(form.zip.trim())) {
      errors.zip = 'Enter a valid ZIP / postal code';
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (!paymentComponentRef.current || !cardReady) {
      setPageError("Payment form isn't ready yet — please wait a moment and try again.");
      return;
    }

    setSubmitting(true);
    const isValid = await paymentComponentRef.current.validate();
    if (!isValid) {
      // validate() has already surfaced inline errors inside the component.
      setSubmitting(false);
      return;
    }
    await paymentComponentRef.current.confirm();
  };

  // Logo-only bar, no menu links — same minimal-topbar pattern
  // CheckoutPage.js uses for its own public checkout flow.
  const topbar = (
    <div className={css.topbar}>
      <a className={css.topbarHome} href="/">
        <img className={css.topbarLogo} src={coachLockupLogo} alt="Viveka" />
      </a>
    </div>
  );

  if (!plan || !planId) {
    return (
      <PageWrapper>
        {topbar}
        <div className={css.base}>
          <div className={css.notFound}>
            <h1 className={css.notFoundTitle}>We couldn't find that plan</h1>
            <p className={css.notFoundText}>
              Please head back and pick a plan from the list.
            </p>
            <a className={css.backLink} href="/get-started">
              &larr; Back to plans
            </a>
          </div>
        </div>
      </PageWrapper>
    );
  }

  const priceLabel = tier === 'lifetime' ? plan.price.oneTime : plan.price[period];
  const priceUnit = tier === 'lifetime' ? ' one-time' : isAnnual ? '/year' : '/month';
  const soldOut = tier === 'lifetime' && lifetime.soldOut;

  return (
    <PageWrapper>
      {topbar}
      <div className={css.base}>
        <a className={css.backLink} href="/get-started">
          &larr; Back to plans
        </a>

        <div className={css.checkoutLayout}>
          <div className={css.leftCol}>
            <span className={css.badge}>{plan.badge}</span>
            <h1 className={css.title}>{plan.title}</h1>
            <div className={css.price}>
              {priceLabel}
              <span className={css.priceUnit}>{priceUnit}</span>
            </div>
            {tier !== 'lifetime' && isAnnual && (
              <div className={css.savingsNote}>{plan.savingsNote}</div>
            )}
            {tier === 'lifetime' && lifetime.seatsRemaining !== null && !soldOut && (
              <div className={css.seatNote}>{lifetime.seatsRemaining} of 100 seats remaining</div>
            )}
            <p className={css.description}>
              {plan.descriptionKicker && (
                <span className={css.descriptionKicker}>{plan.descriptionKicker} </span>
              )}
              {plan.description}
            </p>
            <ul className={css.list}>
              {plan.features.map(feature => (
                <li key={feature.title}>
                  <span className={css.featureText}>
                    <span className={css.featureTitle}>{feature.title}</span> — {feature.detail}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className={css.rightCol}>
            <div className={css.summaryCard}>
              <h2 className={css.summaryTitle}>Subscribe to {plan.title}</h2>

              {pageError && <div className={css.checkoutError}>{pageError}</div>}

              {soldOut ? (
                <div className={css.soldOut}>This plan is sold out.</div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className={css.formRow}>
                    <div className={css.formField}>
                      <label className={css.fieldLabel} htmlFor="cp-first-name">
                        First name
                      </label>
                      <input
                        id="cp-first-name"
                        className={css.textInput}
                        value={form.firstName}
                        onChange={e => {
                          const value = e.target.value;
                          setForm(f => ({ ...f, firstName: value }));
                        }}
                      />
                      {formErrors.firstName && (
                        <div className={css.fieldError}>{formErrors.firstName}</div>
                      )}
                    </div>
                    <div className={css.formField}>
                      <label className={css.fieldLabel} htmlFor="cp-last-name">
                        Last name
                      </label>
                      <input
                        id="cp-last-name"
                        className={css.textInput}
                        value={form.lastName}
                        onChange={e => {
                          const value = e.target.value;
                          setForm(f => ({ ...f, lastName: value }));
                        }}
                      />
                      {formErrors.lastName && (
                        <div className={css.fieldError}>{formErrors.lastName}</div>
                      )}
                    </div>
                  </div>

                  <div className={css.formField}>
                    <label className={css.fieldLabel} htmlFor="cp-email">
                      Email
                    </label>
                    <input
                      id="cp-email"
                      type="email"
                      className={css.textInput}
                      value={form.email}
                      onChange={e => {
                        const value = e.target.value;
                        setForm(f => ({ ...f, email: value }));
                      }}
                    />
                    {formErrors.email && <div className={css.fieldError}>{formErrors.email}</div>}
                  </div>

                  <div className={css.formField}>
                    <label className={css.fieldLabel} htmlFor="cp-address">
                      Billing address
                    </label>
                    <input
                      id="cp-address"
                      className={css.textInput}
                      value={form.addressLine1}
                      onChange={e => {
                        const value = e.target.value;
                        setForm(f => ({ ...f, addressLine1: value }));
                      }}
                      placeholder="Street address"
                    />
                    {formErrors.addressLine1 && (
                      <div className={css.fieldError}>{formErrors.addressLine1}</div>
                    )}
                  </div>

                  <div className={css.formField}>
                    <label className={css.fieldLabel} htmlFor="cp-country">
                      Country
                    </label>
                    <select
                      id="cp-country"
                      className={css.textInput}
                      value={form.country || 'US'}
                      onChange={e => {
                        const value = e.target.value;
                        setForm(f => ({ ...f, country: value }));
                      }}
                    >
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={css.formRow}>
                    <div className={css.formField}>
                      <label className={css.fieldLabel} htmlFor="cp-city">
                        City
                      </label>
                      <input
                        id="cp-city"
                        className={css.textInput}
                        value={form.city}
                        onChange={e => {
                          const value = e.target.value;
                          setForm(f => ({ ...f, city: value }));
                        }}
                      />
                      {formErrors.city && <div className={css.fieldError}>{formErrors.city}</div>}
                    </div>
                    <div className={css.formField}>
                      <label className={css.fieldLabel} htmlFor="cp-state">
                        State
                      </label>
                      <input
                        id="cp-state"
                        className={css.textInput}
                        value={form.state}
                        onChange={e => {
                          const value = e.target.value;
                          setForm(f => ({ ...f, state: value }));
                        }}
                      />
                      {formErrors.state && <div className={css.fieldError}>{formErrors.state}</div>}
                    </div>
                    <div className={css.formField}>
                      <label className={css.fieldLabel} htmlFor="cp-zip">
                        ZIP / Postal code
                      </label>
                      <input
                        id="cp-zip"
                        className={css.textInput}
                        value={form.zip}
                        onChange={e => {
                          const value = e.target.value;
                          setForm(f => ({ ...f, zip: value }));
                        }}
                      />
                      {formErrors.zip && <div className={css.fieldError}>{formErrors.zip}</div>}
                    </div>
                  </div>

                  <div className={css.formField}>
                    <label className={css.fieldLabel} htmlFor="cp-coupon">
                      Coupon code
                    </label>
                    <div className={css.couponRow}>
                      <input
                        id="cp-coupon"
                        className={css.textInput}
                        value={couponCode}
                        onChange={e => {
                          const value = e.target.value;
                          setCouponCode(value);
                          setCouponPreview(null);
                          setCouponError(null);
                          setCouponEligiblePlans(null);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            applyCoupon();
                          }
                        }}
                        placeholder="Optional"
                        disabled={!!couponPreview}
                      />
                      <button
                        type="button"
                        className={css.couponApplyBtn}
                        onClick={applyCoupon}
                        disabled={couponChecking || !couponCode.trim() || !!couponPreview}
                      >
                        {couponChecking ? 'Checking…' : 'Apply'}
                      </button>
                    </div>
                    {couponError && <div className={css.fieldError}>{couponError}</div>}
                    {couponEligiblePlans && couponEligiblePlans.length > 0 && (
                      <div className={css.couponEligiblePlans}>
                        {couponEligiblePlans.map(p => (
                          <a
                            key={`${p.tier}-${p.period}`}
                            className={css.couponSwitchPlanBtn}
                            href={`/checkout-plan/${p.tier}?period=${p.period}`}
                          >
                            Switch to {p.label}
                          </a>
                        ))}
                      </div>
                    )}
                    {couponPreview && (
                      <div className={css.couponSavings}>
                        Coupon applied — you save{' '}
                        {formatMoney(couponPreview.discountAmount, couponPreview.currencyCode)}
                        {couponPreview.discountAmount > 0 && (
                          <>
                            {' '}
                            ({formatMoney(couponPreview.originalPrice, couponPreview.currencyCode)} &rarr;{' '}
                            {formatMoney(couponPreview.finalPrice, couponPreview.currencyCode)})
                          </>
                        )}{' '}
                        <button type="button" className={css.couponRemoveBtn} onClick={removeCoupon}>
                          Remove
                        </button>
                      </div>
                    )}
                  </div>

                  <div className={css.formField}>
                    <label className={css.fieldLabel}>Payment details</label>
                    <div className={css.paymentComponentWrap}>
                      {/* Always rendered (never display:none) so Chargebee's
                          iframe has real dimensions to mount into — the
                          spinner overlays on top instead of replacing it. */}
                      <div id="checkout-plan-payment-component" className={css.paymentComponentMount} />
                      {!cardReady && !pageError && (
                        <div className={css.paymentLoading}>
                          <span className={css.paymentSpinner} />
                          Loading secure payment form…
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    className={css.submitBtn}
                    disabled={submitting || !!pageError}
                  >
                    {submitting ? 'Processing…' : 'Pay & Subscribe'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
}

export default CheckoutPlanPage;
